import angular from "angular";

import { Dates, Invoice, InvoicePreview, Numeric, BicHelper, Iban, InvoicesLogic } from "@salaxy/core";
import { Barcodes, QrCodes } from "@salaxy/reports";
/**
 * Shows an invoice as credit transfer: Either as printable display, copy-pasteable barcode or both.
 */
export class CreditTransferController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$scope", "$sce", "$timeout"];

  /** Invoice that is shown as credit transfer: The main data object. */
  public invoice: Invoice;

  private invoiceQrCode = "";

  /**
   * Display mode is either
   *
   * - "official": The official printable credit transfer (Finanssialan keskusliitto)
   * - "barcode-copy": The copy-paste input + button (in a div) for copy pasting the the barcode into a web bank
   * - "default": Both of the above separted by an explaining text.
   */
  public mode: "default" | "official" | "barcode-copy";

  private _invoicePreview: InvoicePreview;

  /**
   * Creates a new CreditTransferController.
   * @ignore
   */
  constructor(private $scope: angular.IScope, private $sce: angular.ISCEService, private $timeout: angular.ITimeoutService) {
    // Dependency injection if necessary.
  }

  /** Copies the barcode from input to clipboard. */
  public copyBarcode(): void {
    const id = "barcode" + this.getId();
    (document.getElementById(id) as HTMLInputElement).select();
    document.execCommand("copy");
  }

  /** Gets a unique id for the barcode input (from which the copying to clipboard is done). */
  public getId(): number {
    return this.$scope.$id;
  }

  /**
   * Retrieves the QR code for the specified invoice item.
   * If the QR code is not already cached, it generates the QR code and caches it for future use.
   * @returns The QR code string.
   */
  public getQrCode(): string {
    if(!this.invoiceQrCode) {
      this.invoiceQrCode = "loading";
      this.generateQrCodeSvgForInvoice().then((qrCode) => {
        if(!qrCode) {
          this.invoiceQrCode = "error";
          this.$timeout();
          return this.invoiceQrCode;
        } else {
          this.invoiceQrCode = qrCode;
          this.$timeout();
          return this.$sce.trustAsHtml(this.invoiceQrCode);
        }
      });
    }
    if(this.invoiceQrCode === "error") {
      return this.invoiceQrCode;
    }
    return this.$sce.trustAsHtml(this.invoiceQrCode);
  }

  /**
   * Sets the Invoice based on an invoice preview (list item based preview) if the invoice is not empty.
   * I.e. if you wish to set to null, you need to set the invoice null separately.
   * Gets the original preview object if set.
   */
  public get invoicePreview(): InvoicePreview {
    return this._invoicePreview;
  }
  public set invoicePreview(value: InvoicePreview) {
    this._invoicePreview = value;
    if (value?.invoice) {
      this.invoice = this.getInvoice(value);
    }
  }

  /**
   * Retrieves the barcode for the invoice recipient's IBAN.
   * If the IBAN does not start with "fi" or is not provided, an empty string is returned.
   * The barcode is generated using the Barcodes.createBarcode function.
   * @returns The barcode as a string, wrapped in an HTML trust object.
   */
  public getBarcode(): string {
    if (!this.invoice?.recipient.iban || !this.invoice?.recipient.iban.toLowerCase().trim().startsWith("fi")) {
      return "";
    }
    const getDocumentAndSvgNode = () => {
      return {document, svgNode: document.createElementNS("http://www.w3.org/2000/svg", "svg")};
    };

    const result = Barcodes.createBarcode(
      getDocumentAndSvgNode,
      this.invoice.recipient.iban || "",
      this.invoice.header.referenceNumber || "",
      Dates.asDate(this.invoice.header.dueDate),
      Numeric.round(this.invoice.header.total * 100, 0),
      {
        width: 1.42,
        height: 35,
        displayValue: true,
        fontSize: 15
      }
    );
    if(!result) {
      return "";
    }
    return this.$sce.trustAsHtml(new XMLSerializer().serializeToString(result as any));
  }

  /** Barcode value */
  public get bcValue(): string {
    if (!this.invoice?.recipient.iban) {
      return "";
    }
    return Barcodes.getValue(
      this.invoice.recipient.iban || "",
      this.invoice.header.referenceNumber || "",
      Dates.asDate(this.invoice.header.dueDate),
      Numeric.round(this.invoice.header.total * 100, 0));
  }

  /**
   * Generates the QR code for the invoice.
   */
  public async generateQrCodeSvgForInvoice(): Promise<string> {
    if (!this.invoice) {
      return;
    }
    const svg = await QrCodes.getValue(
      this.getBicForIban(this.invoice?.recipient?.iban),
      this.invoice.recipient.fullName,
      this.invoice.recipient.iban,
      this.invoice.header.referenceNumber,
      null,
      this.invoice.header.total,
      this.invoice.header.dueDate
    );
    return svg;
  }

  /**
   * Returns bic for iban
   * @param iban Iban to get bic for
   */
  public getBicForIban(iban: string): string {
    return BicHelper.getBicForIban(iban);
  }

  /**
   * Determines whether to show the QR code for the invoice.
   * @returns A boolean indicating whether to show the QR code.
   */
  public showQrCode(): boolean {
    return InvoicesLogic.isEcfa(this.invoice.header.channel);
  }

  /**
   * Checks if the IBAN of the recipient is an international IBAN.
   * @returns {boolean} True if the IBAN is international, false otherwise.
   */
  public isInternationalIban(): boolean {
    return Iban.isInternationalIban(this.invoice.recipient.iban ?? "");
  }

  /** Maps an invoice preview object to invoice. */
  private getInvoice(preview: InvoicePreview): Invoice {
    if (!preview?.invoice) {
      return null;
    }
    return {
      header: {
        dueDate: preview.invoice.data.dueDate,
        total: preview.invoice.payment,
        referenceNumber: preview.invoice.reference,
      },
      rows: [],
      payer: {
        avatar: preview.invoice.ownerInfo.avatar,
        officialId: preview.invoice.ownerInfo.officialId,
        contact: {
          email: preview.invoice.ownerInfo.email,
          telephone: preview.invoice.ownerInfo.telephone,
        },
      },
      recipient: {
        fullName: preview.invoice.otherPartyInfo.avatar.displayName,
        iban: preview.invoice.otherPartyInfo.ibanNumber,
        officialId: preview.invoice.otherPartyInfo.officialId,
        contact: {
          email: preview.invoice.otherPartyInfo.email,
          telephone: preview.invoice.otherPartyInfo.telephone,
        },
      },
    };
  }
}
